import { Eye, EyeOff, Trash2, X } from "lucide-react";
import { useSvgDiagram2D } from "./SvgDiagram2DContext";
import { DebouncedInput } from "../reusable/DebouncedInput";
import { CheckboxField } from "../reusable/CheckboxField";
import { FillControl } from "../reusable/FillControl";
import { StrokeStyleControl } from "../reusable/StrokeStyleControl";
import { isMultiShapeSelection } from "./SvgDiagram2DMultiSelectPanel";
import { CORNER_STYLES } from "../../utils/svgDiagram2DModel";
import type { CornerStyle } from "../../utils/svgDiagram2DModel";

function num(v: string | number, fallback: number): number {
    const n = typeof v === "number" ? v : Number(v);
    return Number.isFinite(n) ? n : fallback;
}

const CORNER_STYLE_LABELS: Record<CornerStyle, string> = {
    round: "Round",
    bevel: "Bevel",
};

/**
 * Floating inspector for the selected rectangle: edits its two corner points,
 * corner softening (radius + round/bevel), and stroke/fill style. Rendered as
 * an overlay inside the viewer (mirrors the line/arc/ellipse inspectors);
 * closes itself when no rect is selected.
 */
export function SvgDiagram2DRectPanel() {
    const ctx = useSvgDiagram2D();
    const { doc, selectedRectIds, setSelectedRectId, updateRect, removeRect, pushSnapshot } =
        ctx;

    // A multi-shape selection is handled by the shared multi-select panel.
    if (isMultiShapeSelection(ctx)) return null;

    // The inspector edits the first selected rect (matches the other panels).
    const rect = doc.rects.find((r) => r.id === selectedRectIds[0]);
    if (!rect) return null;

    /** Snapshot before a discrete (single-commit) edit so it can be undone. */
    const edit = (partial: Parameters<typeof updateRect>[1]) => {
        pushSnapshot();
        updateRect(rect.id, partial);
    };

    return (
        <div className="diagram-node-panel">
            <div className="field-row" style={{ alignItems: "center", gap: 6 }}>
                <strong style={{ flex: 1, minWidth: 0 }}>
                    Rectangle properties
                </strong>
                <button
                    type="button"
                    className="generic-button"
                    onClick={() => setSelectedRectId(null)}
                    title="Close panel"
                    style={{ padding: "2px 6px" }}
                >
                    <X size={14} aria-hidden />
                </button>
            </div>

            {/* --- Geometry --- */}
            <div className="diagram-node-panel-section">
                <p className="diagram-node-panel-section-title">Geometry</p>
                <label className="field-label">
                    Label
                    <input
                        type="text"
                        value={rect.label}
                        onChange={(e) =>
                            updateRect(rect.id, { label: e.target.value })
                        }
                    />
                </label>
                {(["start", "end"] as const).map((corner) => (
                    <div key={corner} className="field-row field-row--two">
                        {(["x", "y"] as const).map((axis) => (
                            <label key={axis} className="field-label">
                                {`${corner === "start" ? "Corner 1" : "Corner 2"} ${axis.toUpperCase()}`}
                                <DebouncedInput
                                    type="number"
                                    step={1}
                                    value={rect[corner][axis]}
                                    onChange={(v) =>
                                        edit({
                                            [corner]: {
                                                ...rect[corner],
                                                [axis]: num(
                                                    v,
                                                    rect[corner][axis],
                                                ),
                                            },
                                        })
                                    }
                                />
                            </label>
                        ))}
                    </div>
                ))}
                <label className="field-label">
                    Rotation (°)
                    <DebouncedInput
                        type="number"
                        step={1}
                        value={rect.rotation}
                        onChange={(v) => edit({ rotation: num(v, rect.rotation) })}
                    />
                </label>
            </div>

            {/* --- Corners --- */}
            <div className="diagram-node-panel-section">
                <p className="diagram-node-panel-section-title">Corners</p>
                <div className="field-row field-row--two">
                    <label className="field-label">
                        Corner radius
                        <DebouncedInput
                            type="number"
                            min={0}
                            step={1}
                            value={rect.cornerRadius}
                            onChange={(v) =>
                                edit({
                                    cornerRadius: Math.max(
                                        0,
                                        num(v, rect.cornerRadius),
                                    ),
                                })
                            }
                        />
                    </label>
                    {rect.cornerRadius > 0 && (
                        <label className="field-label">
                            Corner style
                            <select
                                value={rect.cornerStyle}
                                onChange={(e) =>
                                    edit({
                                        cornerStyle: e.target
                                            .value as CornerStyle,
                                    })
                                }
                            >
                                {CORNER_STYLES.map((c) => (
                                    <option key={c} value={c}>
                                        {CORNER_STYLE_LABELS[c]}
                                    </option>
                                ))}
                            </select>
                        </label>
                    )}
                </div>
            </div>

            {/* --- Style --- */}
            <div className="diagram-node-panel-section">
                <p className="diagram-node-panel-section-title">Style</p>
                <StrokeStyleControl
                    strokeStyleId={rect.strokeStyleId}
                    strokeStyles={doc.strokeStyles}
                    onChange={(strokeStyleId) => edit({ strokeStyleId })}
                >
                    <div className="field-row field-row--two">
                        <label className="field-label">
                            Stroke
                            <input
                                type="color"
                                value={rect.color}
                                onChange={(e) =>
                                    updateRect(rect.id, {
                                        color: e.target.value,
                                    })
                                }
                            />
                        </label>
                        <label className="field-label">
                            Width
                            <DebouncedInput
                                type="number"
                                min={0.1}
                                step={0.1}
                                value={rect.strokeWidth}
                                onChange={(v) =>
                                    edit({
                                        strokeWidth: Math.max(
                                            0.1,
                                            num(v, rect.strokeWidth),
                                        ),
                                    })
                                }
                            />
                        </label>
                    </div>
                    <label className="field-label">
                        Dash (e.g. "4 2", empty = solid)
                        <input
                            type="text"
                            value={rect.dash}
                            onChange={(e) =>
                                updateRect(rect.id, { dash: e.target.value })
                            }
                        />
                    </label>
                </StrokeStyleControl>
                <div
                    className="field-row"
                    style={{ gap: 8, alignItems: "center" }}
                >
                    <FillControl
                        fill={rect.fill}
                        patterns={doc.patterns}
                        onChange={(fill) => edit({ fill })}
                        label="Rectangle"
                    />
                    <CheckboxField
                        checked={rect.visible}
                        onChange={(checked) =>
                            updateRect(rect.id, { visible: checked })
                        }
                        label={
                            <span
                                style={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: 4,
                                }}
                            >
                                {rect.visible ? (
                                    <Eye size={14} aria-hidden />
                                ) : (
                                    <EyeOff size={14} aria-hidden />
                                )}
                                Visible
                            </span>
                        }
                        inline
                    />
                </div>
            </div>

            {/* --- Danger --- */}
            <div className="diagram-node-panel-section">
                <button
                    type="button"
                    className="generic-button"
                    onClick={() => removeRect(rect.id)}
                    title="Delete this rectangle"
                >
                    <Trash2 size={14} aria-hidden /> Delete rectangle
                </button>
            </div>
        </div>
    );
}
